import { useEffect, useState } from "react";
import {
  acceptAll,
  rejectAll,
  setConsent,
  getConsentState,
  hasUserDecided,
  type ConsentState,
} from "@/lib/consent";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "wouter";
import { X, Cookie } from "lucide-react";

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [state, setState] = useState<ConsentState>(() => getConsentState());

  useEffect(() => {
    if (!hasUserDecided()) setVisible(true);
    const open = () => {
      setShowSettings(true);
      setVisible(true);
      setState(getConsentState());
    };
    window.addEventListener("rb:open-cookie-settings", open);
    return () => window.removeEventListener("rb:open-cookie-settings", open);
  }, []);

  if (!visible) return null;

  const close = () => {
    setVisible(false);
    setShowSettings(false);
  };

  const handleAccept = () => {
    setState(acceptAll());
    close();
  };

  const handleReject = () => {
    setState(rejectAll());
    close();
  };

  const handleSavePrefs = () => {
    setState(
      setConsent({
        analytics: state.analytics,
        marketing: state.marketing,
        preferences: state.preferences,
      }),
    );
    close();
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 bg-white dark:bg-neutral-900 border-t border-border shadow-2xl"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 sm:py-5">
        {!showSettings ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-foreground">
                We use cookies and similar technologies to operate this site,
                measure traffic, and improve your experience. You can accept,
                reject non-essential cookies, or pick categories. Read our{" "}
                <Link href="/legal/privacy" className="underline hover:text-primary" data-testid="link-banner-privacy">
                  Privacy Policy
                </Link>{" "}
                and{" "}
                <Link href="/legal/cookies" className="underline hover:text-primary" data-testid="link-banner-cookies">
                  Cookie Policy
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 md:shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(true)}
                data-testid="cookie-customize"
              >
                Customise
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleReject}
                data-testid="cookie-reject"
              >
                Reject non-essential
              </Button>
              <Button size="sm" onClick={handleAccept} data-testid="cookie-accept">
                Accept all
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-base font-semibold text-foreground">Cookie preferences</h2>
                <p className="text-sm text-muted-foreground">
                  Necessary cookies are always on. You can opt out of others at any time.
                </p>
              </div>
              <button
                onClick={close}
                aria-label="Close"
                className="text-muted-foreground hover:text-foreground"
                data-testid="cookie-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Row
                title="Strictly necessary"
                desc="Required for login, security, and core site features. Always on."
                checked
                disabled
                onChange={() => {}}
                testId="cookie-row-necessary"
              />
              <Row
                title="Preferences"
                desc="Remember language, theme, and helpful UI choices."
                checked={state.preferences}
                onChange={(v) => setState({ ...state, preferences: v })}
                testId="cookie-row-preferences"
              />
              <Row
                title="Analytics"
                desc="Aggregated traffic and usage stats (Google Analytics 4)."
                checked={state.analytics}
                onChange={(v) => setState({ ...state, analytics: v })}
                testId="cookie-row-analytics"
              />
              <Row
                title="Marketing"
                desc="Ads measurement, retargeting, and Google Maps personalised features."
                checked={state.marketing}
                onChange={(v) => setState({ ...state, marketing: v })}
                testId="cookie-row-marketing"
              />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={handleReject} data-testid="cookie-reject-prefs">
                Reject all
              </Button>
              <Button variant="outline" size="sm" onClick={handleSavePrefs} data-testid="cookie-save">
                Save my choices
              </Button>
              <Button size="sm" onClick={handleAccept} data-testid="cookie-accept-prefs">
                Accept all
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({
  title,
  desc,
  checked,
  disabled,
  onChange,
  testId,
}: {
  title: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
  testId: string;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border p-3" data-testid={testId}>
      <div className="text-sm">
        <div className="font-medium text-foreground">{title}</div>
        <div className="text-muted-foreground">{desc}</div>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}
